import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext();

// Cart actions
const CART_ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  LOAD_CART: 'LOAD_CART',
};

// Helper: genera la clave de localStorage según el usuario
function getCartStorageKey(user) {
  if (user && user.id) return `tablero-infinito-cart-user-${user.id}`;
  return 'tablero-infinito-cart-guest';
}

/**
 * Identificador de línea de carrito.
 *
 * Prefiere el id real del producto y solo cae al slug del nombre cuando no
 * hay. Antes leía `product.ID` (mayúsculas), que normalizeProduct nunca
 * produce, así que en la práctica *siempre* usaba el slug.
 */
export function getProductId(product) {
  const id = product?.id ?? product?.ID;
  if (id !== undefined && id !== null && id !== '') return String(id);
  return product?.Juego?.replace(/\s+/g, '-').toLowerCase() || `product-${Date.now()}`;
}

/**
 * Empareja una línea del carrito con un producto.
 *
 * Compara también contra el slug del nombre porque los carritos que los
 * usuarios ya tienen guardados en localStorage se escribieron con el id
 * antiguo (el slug). Sin esta compatibilidad, tras el despliegue un producto
 * ya añadido aparecería como "no está en el carrito" y se duplicaría la línea.
 */
export function matchesProduct(item, product, productId = getProductId(product)) {
  if (item.id === productId) return true;
  if (item.productId != null && product?.id != null) {
    return String(item.productId) === String(product.id);
  }
  return false;
}

// Cart reducer
function cartReducer(state, action) {
  switch (action.type) {
    case CART_ACTIONS.ADD_ITEM: {
      const { product, quantity = 1 } = action.payload;
      const cantidad = Math.max(1, Number(quantity) || 1);

      const productId = getProductId(product);
      const existingItem = state.items.find(item => matchesProduct(item, product, productId));

      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            matchesProduct(item, product, productId)
              ? { ...item, quantity: item.quantity + cantidad }
              : item
          ),
        };
      }

      const newItem = {
        id: productId,
        productId: product.id ?? product.ID ?? null,
        name: product.Juego,
        price:
          product.Oferta?.toLowerCase() === 'si'
            ? (product.Precio_Oferta || 0)
            : (product.Precio_Publico || 0),
        originalPrice: product.Precio_Publico || 0,
        image: product.Imagen,
        // normalizeProduct expone `marca`; `brand` nunca existió, así que la
        // editorial salía vacía en el carrito.
        brand: product.marca ?? product.brand ?? null,
        isOnSale: product.Oferta?.toLowerCase() === 'si',
        isPreorder: product.Preventa?.toLowerCase() === 'si',
        quantity: cantidad,
      };

      return {
        ...state,
        items: [...state.items, newItem],
      };
    }

    case CART_ACTIONS.REMOVE_ITEM:
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload.id),
      };

    case CART_ACTIONS.UPDATE_QUANTITY: {
      const { id, quantity } = action.payload;

      if (quantity <= 0) {
        return {
          ...state,
          items: state.items.filter(item => item.id !== id),
        };
      }

      return {
        ...state,
        items: state.items.map(item => (item.id === id ? { ...item, quantity } : item)),
      };
    }

    case CART_ACTIONS.CLEAR_CART:
      return {
        ...state,
        items: [],
      };

    case CART_ACTIONS.LOAD_CART:
      return {
        ...state,
        items: action.payload.items || [],
      };

    default:
      return state;
  }
}

// Initial state
const initialState = {
  items: [],
};

// CartProvider component
export function CartProvider({ children }) {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const prevUserIdRef = useRef(undefined);
  const isInitialLoadRef = useRef(true);

  // Función para cargar carrito desde localStorage con una clave específica
  const loadCartFromStorage = useCallback((storageKey) => {
    try {
      const savedCart = localStorage.getItem(storageKey);
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        if (parsedCart && Array.isArray(parsedCart.items)) {
          dispatch({ type: CART_ACTIONS.LOAD_CART, payload: { items: parsedCart.items } });
          return;
        } else {
          localStorage.removeItem(storageKey);
        }
      }
      // Si no hay carrito guardado, limpiar el estado
      dispatch({ type: CART_ACTIONS.LOAD_CART, payload: { items: [] } });
    } catch (error) {
      console.error('Error al cargar el carrito desde localStorage:', error);
      localStorage.removeItem(storageKey);
      dispatch({ type: CART_ACTIONS.LOAD_CART, payload: { items: [] } });
    }
  }, []);

  // Recargar carrito cuando cambia el usuario (login/logout)
  useEffect(() => {
    const currentUserId = user?.id || null;
    const prevUserId = prevUserIdRef.current;

    // En el primer render, solo cargamos
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      const key = getCartStorageKey(user);
      loadCartFromStorage(key);
      prevUserIdRef.current = currentUserId;
      return;
    }

    // Solo actuar si cambió el usuario
    if (currentUserId !== prevUserId) {
      // Guardar carrito actual del usuario anterior antes de cambiar
      if (state.items.length > 0) {
        const prevKey = getCartStorageKey(prevUserId ? { id: prevUserId } : null);
        const cartToSave = { items: state.items, lastUpdated: new Date().toISOString() };
        localStorage.setItem(prevKey, JSON.stringify(cartToSave));
      }

      // Cargar carrito del nuevo usuario (o guest)
      const newKey = getCartStorageKey(user);
      loadCartFromStorage(newKey);
      prevUserIdRef.current = currentUserId;
    }
  }, [user, loadCartFromStorage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guardar carrito en localStorage con clave del usuario actual
  useEffect(() => {
    // No guardar durante la carga inicial
    if (isInitialLoadRef.current) return;

    const storageKey = getCartStorageKey(user);
    if (state.items.length > 0) {
      try {
        const cartToSave = {
          items: state.items,
          lastUpdated: new Date().toISOString(),
        };
        localStorage.setItem(storageKey, JSON.stringify(cartToSave));
      } catch (error) {
        console.error('Error al guardar el carrito en localStorage:', error);
      }
    } else {
      localStorage.removeItem(storageKey);
    }
  }, [state, user]);

  // Cart actions
  const addToCart = (product, quantity = 1) => {
    dispatch({ type: CART_ACTIONS.ADD_ITEM, payload: { product, quantity } });
  };

  const removeFromCart = id => {
    dispatch({ type: CART_ACTIONS.REMOVE_ITEM, payload: { id } });
  };

  const updateQuantity = (productOrId, quantity) => {
    let id;
    if (typeof productOrId === 'string') {
      id = productOrId;
    } else {
      // Se resuelve contra las líneas que ya hay en el carrito para no fallar
      // con las guardadas antes del cambio de identificador (ver
      // matchesProduct); si no está, se usa el id nuevo.
      const existente = state.items.find(item => matchesProduct(item, productOrId));
      id = existente ? existente.id : getProductId(productOrId);
    }
    dispatch({ type: CART_ACTIONS.UPDATE_QUANTITY, payload: { id, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
  };

  const getItemQuantity = productOrId => {
    if (typeof productOrId === 'string') {
      const item = state.items.find(item => item.id === productOrId);
      return item ? item.quantity : 0;
    }
    const item = state.items.find(item => matchesProduct(item, productOrId));
    return item ? item.quantity : 0;
  };

  // Cart calculations
  const getTotalItems = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return state.items.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getTotalSavings = () => {
    return state.items.reduce((total, item) => {
      if (item.isOnSale) {
        const savings = (item.originalPrice - item.price) * item.quantity;
        return total + savings;
      }
      return total;
    }, 0);
  };

  const value = {
    items: state.items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getItemQuantity,
    getTotalItems,
    getTotalPrice,
    getTotalSavings,
    isEmpty: state.items.length === 0,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// Custom hook to use cart context
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
